'use memo'

import { AgxBuyersAgentSearch } from '@urbanx/agx-ui-components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useFeatureToggles,
  useSelectedCampaign,
  useSelectedContract,
} from 'hooks'
import { MenuTitles, Pages } from 'constants/menu'
import { ContractSubPage } from '../ContractSubPage'
import { getBuyerDetailsSubPages } from './BuyerDetailsSubPages'
import {
  BuyersAgentModel,
  useInvalidateUseStaffGetBuyersAgent,
  useStaffGetBuyersAgent,
  useStaffUpdateBuyersAgent,
} from '@urbanx/agx.campaigns.client'
import { ErrorModal } from 'helpers/ErrorModal'
import { isDirtyEqual } from 'utils/compares'
import { SubPagePanel } from 'components/panel/SubPagePanel'

const BuyersAgent = () => {
  const { campaign } = useSelectedCampaign()
  const { contract } = useSelectedContract()
  const { featureToggles } = useFeatureToggles()
  const [isLoaded, setIsLoaded] = useState(false)
  const [validate, setValidate] = useState(false)
  const [openErrorModal, setOpenErrorModal] = useState(false)
  const [submitFormErrors, setSubmitFormErrors] = useState<string[]>([])
  const invalidateUseStaffGetBuyersAgent = useInvalidateUseStaffGetBuyersAgent()

  const {
    data: buyersAgent,
    isPending,
    isError,
  } = useStaffGetBuyersAgent(contract?.contractId || '', !!contract?.contractId)

  const [value, setValue] = useState<BuyersAgentModel | undefined>(buyersAgent)

  useEffect(() => {
    setValue(buyersAgent)
    setIsLoaded(!isPending)
  }, [buyersAgent, isPending])

  useEffect(() => {
    setValidate(!isDirtyEqual(value, buyersAgent))
  }, [value])

  const { staffUpdateBuyersAgent, staffUpdateBuyersAgentPending } =
    useStaffUpdateBuyersAgent({
      onSuccess: () => {},
      onError: () => {},
    })

  const isFormDirty = useMemo(() => {
    return !isDirtyEqual(value, buyersAgent)
  }, [value, buyersAgent])
  const showIdentityVerification = featureToggles?.identityVerification === true

  const onSave = useCallback(
    async (success: () => void) => {
      if (!value || !contract?.contractId) return

      await staffUpdateBuyersAgent(
        {
          body: value,
          contractId: contract.contractId,
        },
        {
          onSuccess: async (data) => {
            if (data?.success) {
              success()
              await invalidateUseStaffGetBuyersAgent(contract.contractId)
            } else {
              setSubmitFormErrors(data?.errors ?? [])
              setOpenErrorModal(true)
            }
          },
        }
      )
    },
    [value, contract?.contractId, staffUpdateBuyersAgent]
  )

  if (!isLoaded) return

  return (
    <ContractSubPage
      key={`BuyersAgent${value ? 'Loaded' : ''}`}
      pageTitle="Buyer Details"
      secondaryActiveMenu={MenuTitles.BUYERS_DETAILS}
      currentPage={Pages.BUYERS_AGENT}
      subPages={getBuyerDetailsSubPages(
        campaign?.state,
        showIdentityVerification
      )}
      onSaveChanges={onSave}
      isFormDirty={isFormDirty}
      isSaving={staffUpdateBuyersAgentPending}
      isError={isError}
    >
      <SubPagePanel
        key={`BuyersAgentPanel${value ? 'Loaded' : ''}`}
        panelTitle="Buyers Agent"
        expandable
        defaultExpanded
      >
        {!isPending && (
          <AgxBuyersAgentSearch
            key={`BuyersAgentSearch${value ? 'Loaded' : ''}`}
            id="BuyersAgent"
            label=""
            onValueUpdated={({ value }) => setValue(value)}
            defaultValue={value}
            validate={validate}
          />
        )}
      </SubPagePanel>
      {openErrorModal && (
        <ErrorModal
          errorList={submitFormErrors}
          setShowModal={setOpenErrorModal}
        />
      )}
    </ContractSubPage>
  )
}

export default BuyersAgent
